/**
 * VisitActions — adaptive action-bar for a visit.
 *
 * Given a visit (appointment + queue entry) and the current user's role,
 * this component consults `visibleActions(mode, role, status)` and renders
 * ONLY the buttons that make sense for the current clinic's operating mode.
 *
 * Callers do NOT need to know about workflow modes — they just pass the
 * visit data + a callback per action. Callbacks are optional; unhandled
 * clicks are silently ignored so callers can opt in gradually.
 *
 *   <VisitActions
 *     appointment={appt}
 *     queueEntry={queue}
 *     onStartVisit={...}
 *     onCheckIn={...}
 *     onMarkInChair={...}
 *     onComplete={...}
 *     onOpenChairside={...}
 *     onOpenBilling={...}
 *     onCancel={...}
 *   />
 */
import { useMemo } from "react";
import { UserCheck, PlayCircle, Armchair, CheckCircle2, ExternalLink, Receipt, X, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useClinic } from "@/lib/useClinic";
import {
  computeVisitStatus,
  modeFromClinic,
  visibleActions,
  STATUS_META,
} from "@/lib/workflowMode";

const ACTION_META = {
  start_visit:    { label: "Start Visit",         icon: PlayCircle,   variant: "primary" },
  check_in:       { label: "Check in patient",    icon: UserCheck,    variant: "primary" },
  assign_chair:   { label: "Assign chair",        icon: Armchair,     variant: "primary" },
  mark_in_chair:  { label: "Mark in chair",       icon: Armchair,     variant: "primary" },
  complete:       { label: "Complete visit",      icon: CheckCircle2, variant: "primary" },
  open_chairside: { label: "Open chairside",      icon: ExternalLink, variant: "secondary" },
  open_billing:   { label: "Bill this visit",     icon: Receipt,      variant: "primary" },
  cancel:         { label: "Cancel",              icon: X,            variant: "muted" },
};

const VARIANT_CLASS = {
  primary:
    "inline-flex items-center justify-center gap-2 rounded-full bg-pine text-white hover:bg-pine/90 px-4 py-2.5 text-sm font-medium",
  secondary:
    "inline-flex items-center justify-center gap-2 rounded-full border border-pine text-pine hover:bg-pine/5 px-4 py-2.5 text-sm font-medium",
  muted:
    "inline-flex items-center justify-center gap-2 rounded-full border border-[#B94A48]/30 text-[#B94A48] hover:bg-[#B94A48]/5 px-4 py-2.5 text-sm",
};

export default function VisitActions({
  appointment,
  queueEntry,
  onStartVisit,
  onCheckIn,
  onAssignChair,
  onMarkInChair,
  onComplete,
  onOpenChairside,
  onOpenBilling,
  onCancel,
  layout = "stack", // "stack" | "row"
}) {
  const { user } = useAuth();
  const { clinic } = useClinic();
  const mode = modeFromClinic(clinic);
  const status = useMemo(
    () => computeVisitStatus(appointment, queueEntry),
    [appointment, queueEntry]
  );
  const actions = useMemo(
    () => visibleActions(mode, user?.role, status),
    [mode, user?.role, status]
  );

  const handlerMap = {
    start_visit: onStartVisit,
    check_in: onCheckIn,
    assign_chair: onAssignChair,
    mark_in_chair: onMarkInChair,
    complete: onComplete,
    open_chairside: onOpenChairside,
    open_billing: onOpenBilling,
    cancel: onCancel,
  };

  // Only render buttons whose handler was supplied — this lets pages opt in
  // one action at a time without noisy no-op buttons.
  const rendered = actions.filter((a) => typeof handlerMap[a] === "function");
  if (rendered.length === 0) return null;

  const meta = STATUS_META[status] || {};
  return (
    <div className="space-y-2" data-testid="visit-actions">
      <div
        className="text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
        style={{ color: meta.color, background: meta.bg }}
        data-testid={`visit-status-${status}`}
      >
        <Sparkles className="w-3 h-3" />
        {meta.label || status}
      </div>
      <div className={layout === "row" ? "flex gap-2 flex-wrap" : "flex flex-col gap-2"}>
        {rendered.map((a) => {
          const A = ACTION_META[a];
          if (!A) return null;
          const Icon = A.icon;
          return (
            <button
              key={a}
              type="button"
              data-testid={`visit-action-${a}`}
              onClick={handlerMap[a]}
              className={VARIANT_CLASS[A.variant]}
            >
              <Icon className="w-4 h-4" /> {A.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
